import React from "react";
import { Box, Card } from "@mui/joy";
import type {
  Excursion,
  ExcursionTemplateLimitsItem,
  ExcursionPlanningCardContext,
} from "api/excursions/Excursion.typed";
import {
  excursionToMinutes,
  assignLanes,
  roundToHalfHour,
  HALF_HOUR,
  toDateKey,
} from "../../shared/utils/planningUtils";
import { EXCURSION_CARD_FALLBACK_COLOR } from "utils/colorPalette";
import ExcursionCardContent from "./ExcursionCardContent";
import ExcursionPlanningCardPopover from "./ExcursionPlanningCardPopover";
import ExcursionDropPendingOverlay from "../../../shared/excursionModal/display/ExcursionDropPendingOverlay";
import {
  excursionCardGlassBorderStrong,
  excursionPlanningProjectionHighlightSx,
  excursionResizeHandleHoverSx,
} from "styles/globalStyles";
import {
  createPlanningExcursionCardClickHandler,
  usePlanningGroupsDrawerOpenSchedule,
} from "../hooks/usePlanningGroupsDrawerOpenSchedule";

export type ExcursionWeekAgendaDayProps = ExcursionPlanningCardContext & {
  day: Date;
  pixelsPerHour: number;
  hoursStart: number;
  hoursEnd: number;
  dayExcursions: Excursion[];
  openModalForSlot: (day: Date, startMinutes: number, endMinutes: number) => void;
  onExcursionDrop: (
    day: Date,
    startMinutes: number,
    endMinutes: number,
    excursionId: number,
  ) => void;
  onExcursionResize?: (excursionId: number, newStartMinutes: number, newEndMinutes: number) => void;
  onExcursionDragOver?: (day: Date, startMinutes: number, durationMinutes: number) => void;
  draggingExcursion: { durationMinutes: number; dragOffsetY: number } | null;
  dragProjection: { day: Date; startMinutes: number } | null;
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
};

export default function ExcursionWeekAgendaDay(props: ExcursionWeekAgendaDayProps) {
  const {
    day,
    pixelsPerHour,
    hoursStart,
    hoursEnd,
    dayExcursions,
    openModalForSlot,
    onExcursionDrop,
    onExcursionResize,
    onExcursionDragOver,
    draggingExcursion,
    dragProjection,
    ...planningPopoverContext
  } = props;
  const {
    openPlanningGroupsDrawer,
    makeExcursionCardDragHandlers,
    makeExcursionCardDropHandlers,
    onDragEnterCard,
    onDragLeaveCard,
    canEdit,
    canOpenExcursionDrawer,
    excursionTemplatesList,
    chipDropPending,
    onOpenExcursionFullModal,
  } = planningPopoverContext;
  const { scheduleOpenDrawer, cancelScheduledDrawer } = usePlanningGroupsDrawerOpenSchedule();
  const columnRef = React.useRef<HTMLDivElement>(null);
  const resizeStateRef = React.useRef<{
    excursionId: number;
    startMinutes: number;
    endMinutes: number;
  } | null>(null);
  const [resizing, setResizing] = React.useState<{
    excursionId: number;
    edge: "top" | "bottom";
    startY: number;
    startMinutes: number;
    endMinutes: number;
  } | null>(null);

  const excursions = dayExcursions ?? [];
  const withMinutes = excursions.map((ex) => ({ ex, ...excursionToMinutes(ex) }));
  const lanes = assignLanes(
    withMinutes,
    (x) => x.startMinutes,
    (x) => x.endMinutes,
  );
  const baseMinutes = hoursStart * 60;
  const totalMinutes = (hoursEnd - hoursStart) * 60;

  const yToStartMinutes = React.useCallback(
    (y: number) => {
      const start = baseMinutes + (y / pixelsPerHour) * 60;
      const clamped = Math.max(baseMinutes, Math.min(baseMinutes + totalMinutes, start));
      return roundToHalfHour(clamped);
    },
    [baseMinutes, totalMinutes, pixelsPerHour],
  );

  const pointerYToSlotTopY = (e: React.DragEvent, rect: DOMRect) => {
    const pointerY = e.clientY - rect.top;
    const offsetY = draggingExcursion?.dragOffsetY ?? 0;
    return pointerY - offsetY;
  };

  const handleColumnDragOver = (e: React.DragEvent) => {
    if (!canEdit || !onExcursionDragOver || !draggingExcursion) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const y = pointerYToSlotTopY(e, rect);
    const startMinutes = yToStartMinutes(y);
    const durationRounded = roundToHalfHour(Math.max(HALF_HOUR, draggingExcursion.durationMinutes));
    onExcursionDragOver(day, startMinutes, durationRounded);
  };

  const handleColumnDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/json");
    if (!raw || !canEdit) return;
    try {
      const d = JSON.parse(raw) as {
        type: string;
        excursionId?: number;
        durationMinutes?: number;
        dragOffsetY?: number;
      };
      if (
        d.type !== "excursion" ||
        typeof d.excursionId !== "number" ||
        typeof d.durationMinutes !== "number"
      )
        return;
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetY =
        typeof d.dragOffsetY === "number" ? d.dragOffsetY : (draggingExcursion?.dragOffsetY ?? 0);
      const y = e.clientY - rect.top - offsetY;
      const startMinutes = yToStartMinutes(y);
      const durationRounded = roundToHalfHour(Math.max(HALF_HOUR, d.durationMinutes));
      const endMinutes = Math.min(startMinutes + durationRounded, baseMinutes + totalMinutes);
      onExcursionDrop(day, startMinutes, endMinutes, d.excursionId);
    } catch {
      /* ignore */
    }
  };

  const handleColumnDoubleClick = (e: React.MouseEvent) => {
    const el = e.target as HTMLElement;
    if (
      !canEdit ||
      el.closest?.("[data-excursion-card]") ||
      el.closest?.("[data-excursion-tooltip]")
    )
      return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const startMinutes = yToStartMinutes(y);
    const endMinutes = roundToHalfHour(startMinutes + 60);
    openModalForSlot(day, startMinutes, endMinutes);
  };

  const isProjectionHere =
    dragProjection && draggingExcursion && toDateKey(dragProjection.day) === toDateKey(day);
  const projectionStart = isProjectionHere ? dragProjection!.startMinutes : 0;
  const projectionEnd = isProjectionHere ? projectionStart + draggingExcursion!.durationMinutes : 0;
  const projectionVisStart = Math.max(projectionStart, baseMinutes);
  const projectionVisEnd = Math.min(projectionEnd, baseMinutes + totalMinutes);

  React.useEffect(() => {
    if (!resizing || !onExcursionResize || !columnRef.current) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!columnRef.current) return;
      const rect = columnRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const newMinutes = yToStartMinutes(y);
      setResizing((prev) => {
        if (!prev) return null;
        let next: typeof prev;
        if (prev.edge === "top") {
          const clamped = Math.max(baseMinutes, Math.min(prev.endMinutes - HALF_HOUR, newMinutes));
          next = { ...prev, startMinutes: clamped };
        } else {
          const clamped = Math.min(
            baseMinutes + totalMinutes,
            Math.max(prev.startMinutes + HALF_HOUR, newMinutes),
          );
          next = { ...prev, endMinutes: clamped };
        }
        resizeStateRef.current = {
          excursionId: next.excursionId,
          startMinutes: next.startMinutes,
          endMinutes: next.endMinutes,
        };
        return next;
      });
    };
    const handleMouseUp = () => {
      const current = resizeStateRef.current;
      if (current && onExcursionResize) {
        const startRounded = roundToHalfHour(current.startMinutes);
        const endRounded = roundToHalfHour(current.endMinutes);
        const endSafe = Math.max(endRounded, startRounded + HALF_HOUR);
        onExcursionResize(current.excursionId, startRounded, endSafe);
      }
      resizeStateRef.current = null;
      setResizing(null);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing, onExcursionResize, baseMinutes, totalMinutes, yToStartMinutes]);

  const handleResizeStart = React.useCallback(
    (e: React.MouseEvent, ex: Excursion, edge: "top" | "bottom") => {
      e.preventDefault();
      e.stopPropagation();
      if (!canEdit || !onExcursionResize) return;
      const { startMinutes, endMinutes } = excursionToMinutes(ex);
      const state = {
        excursionId: ex.id,
        edge,
        startY: e.clientY,
        startMinutes,
        endMinutes,
      };
      resizeStateRef.current = { excursionId: ex.id, startMinutes, endMinutes };
      setResizing(state);
    },
    [canEdit, onExcursionResize],
  );

  return (
    <Box
      ref={columnRef}
      onDoubleClick={handleColumnDoubleClick}
      onDragOver={canEdit ? handleColumnDragOver : undefined}
      onDrop={canEdit ? handleColumnDrop : undefined}
      sx={{
        position: "absolute",
        inset: 0,
        cursor: canEdit ? "pointer" : "default",
      }}
    >
      {isProjectionHere && projectionVisEnd > projectionVisStart && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `${((projectionVisStart - baseMinutes) / 60) * pixelsPerHour + 2}px`,
            height: `${((projectionVisEnd - projectionVisStart) / 60) * pixelsPerHour - 4}px`,
            minHeight: 24,
            borderRadius: "6px",
            ...excursionPlanningProjectionHighlightSx,
            pointerEvents: "none",
            boxSizing: "border-box",
            zIndex: 10,
          }}
        />
      )}
      {withMinutes.map(({ ex }, idx) => {
        const resizingThis = resizing?.excursionId === ex.id;
        const startMinutes = resizingThis ? resizing!.startMinutes : withMinutes[idx].startMinutes;
        const endMinutes = resizingThis ? resizing!.endMinutes : withMinutes[idx].endMinutes;
        const visStart = Math.max(startMinutes, baseMinutes);
        const visEnd = Math.min(endMinutes, baseMinutes + totalMinutes);
        if (visStart >= visEnd) return null;
        const top = ((visStart - baseMinutes) / 60) * pixelsPerHour;
        const height = ((visEnd - visStart) / 60) * pixelsPerHour;
        const { lane, totalLanes } = lanes[idx];
        const gap = 3;
        const widthCalc =
          totalLanes <= 1 ? "100%" : `calc((100% - ${(totalLanes - 1) * gap}px) / ${totalLanes})`;
        const leftCalc =
          totalLanes <= 1
            ? "0"
            : `calc(${lane} * ( (100% - ${(totalLanes - 1) * gap}px) / ${totalLanes} + ${gap}px ))`;
        const dropHandlers = makeExcursionCardDropHandlers(ex.id) as {
          onDragOver?: (e: React.DragEvent) => void;
          onDrop?: (e: React.DragEvent) => void;
        };
        const isCancelled = ex.status === "cancelled";
        const cardMuted = isCancelled || ex.status === "validated";
        return (
          <Box
            key={ex.id}
            sx={{
              position: "absolute",
              left: leftCalc,
              width: widthCalc,
              top: `${top + 2}px`,
              height: `${height - 4}px`,
              minHeight: 48,
            }}
          >
            {canEdit && onExcursionResize && !isCancelled && (
              <>
                <Box
                  component="span"
                  onMouseDown={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleResizeStart(e, ex, "top");
                  }}
                  sx={{
                    position: "absolute",
                    top: -4,
                    left: 0,
                    right: 0,
                    height: 16,
                    cursor: "ns-resize",
                    zIndex: 5,
                    ...excursionResizeHandleHoverSx,
                  }}
                />
                <Box
                  component="span"
                  onMouseDown={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleResizeStart(e, ex, "bottom");
                  }}
                  sx={{
                    position: "absolute",
                    bottom: -4,
                    left: 0,
                    right: 0,
                    height: 16,
                    cursor: "ns-resize",
                    zIndex: 5,
                    ...excursionResizeHandleHoverSx,
                  }}
                />
              </>
            )}
            <ExcursionPlanningCardPopover excursion={ex} {...planningPopoverContext}>
              <Card
                data-excursion-card
                component="div"
                {...makeExcursionCardDragHandlers(ex)}
                onClick={createPlanningExcursionCardClickHandler({
                  canOpenExcursionDrawer,
                  canEdit,
                  onOpenExcursionFullModal,
                  openPlanningGroupsDrawer,
                  scheduleOpenDrawer,
                  cancelScheduledDrawer,
                  excursion: ex,
                })}
                onDrop={dropHandlers.onDrop}
                onDragOver={(e: React.DragEvent) => {
                  if (canEdit && draggingExcursion && onExcursionDragOver) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    const startRounded = roundToHalfHour(startMinutes);
                    const durationRounded = roundToHalfHour(
                      Math.max(HALF_HOUR, draggingExcursion.durationMinutes),
                    );
                    onExcursionDragOver(day, startRounded, durationRounded);
                  }
                  dropHandlers.onDragOver?.(e);
                }}
                onDragEnter={
                  canEdit
                    ? (e: React.DragEvent) => {
                        e.stopPropagation();
                        onDragEnterCard(ex.id);
                      }
                    : undefined
                }
                onDragLeave={
                  canEdit
                    ? (e: React.DragEvent) => {
                        e.stopPropagation();
                        onDragLeaveCard(ex.id, e);
                      }
                    : undefined
                }
                sx={{
                  position: "absolute",
                  inset: 0,
                  boxSizing: "border-box",
                  backgroundColor: ex.color ?? EXCURSION_CARD_FALLBACK_COLOR,
                  opacity: cardMuted ? 0.5 : 1,
                  border: excursionCardGlassBorderStrong,
                  borderRadius: "8px",
                  p: 0.5,
                  overflow: "hidden",
                  cursor: canEdit || canOpenExcursionDrawer ? "pointer" : "default",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  transition: "box-shadow 0.2s ease",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
                  "&:hover": {
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.28)",
                  },
                }}
              >
                <ExcursionCardContent
                  excursion={ex}
                  layout="agenda"
                  excursionTemplatesList={excursionTemplatesList}
                />
                <ExcursionDropPendingOverlay
                  show={
                    chipDropPending?.excursionId === ex.id && chipDropPending.surface === "card"
                  }
                />
              </Card>
            </ExcursionPlanningCardPopover>
          </Box>
        );
      })}
    </Box>
  );
}
